import { getDateDifference, isEqual, parseDate } from '@travelata-libs/utils';
import type { SerpHotelPosition, TrackingData, TrackingMeta } from '@/entities/analytics';
import type { Product, ProductSearchResult, ProductSearchSnapshot } from '@/entities/product';
import { buildSearchCriteriaParams, type SearchCriteria } from '@/entities/searchCriteria';
import type { SearchCriteriaParams } from '@/shared/model';
import type { LowPriceCalendarLoad } from '@/widgets/lowPriceCalendar';
import { getSelectedFilterKeys } from './filtersModel';
import { getOperatorsFilterOptions } from './getFiltersGroups';
import { isSearchFilterGroup } from './searchFilters';
import { getSerpResultsDictionaries } from './serpResultsDictionaries';
import { SERP_FILTER_GROUPS } from '../config/filters';
import {
  SERP_TRACKING_COUNTRIES_COUNT,
  SERP_TRACKING_FAILED_PHASES,
  SERP_TRACKING_FILTER_FIELDS,
  SERP_TRACKING_HOTEL_CATEGORY_FIELDS,
  SERP_TRACKING_HOTELS_COUNT,
  SERP_TRACKING_MEAL_FIELDS,
  SERP_TRACKING_RESORTS_COUNT,
  type SerpTrackingFilterOption,
} from '../config/tracking';
import { FilterGroupId, type FiltersModel, type SerpProduct } from '../model/types';

type TrackingFlag = 0 | 1;

type TouristGroup = SearchCriteriaParams['touristGroup'];

interface TrackingRange {
  from: string;
  to: string;
}

interface TrackingRoom {
  adults: number;
  kids: number;
  infants: number;
}

interface TrackingTourists extends TrackingRoom {
  rooms: TrackingRoom[] | 0;
}

interface SerpStartSearchTrackingOptions {
  productType: string;
  searchUuid: string;
  criteria: SearchCriteria;
  personalProfile: boolean;
  meta: TrackingMeta;
}

interface SerpStartEmptySearchTrackingOptions extends SerpStartSearchTrackingOptions {
  originalSearchUuid: string;
}

interface SerpSearchResultsTrackingOptions {
  productType: string;
  result: ProductSearchResult<Product>;
  criteria: SearchCriteria;
  firstHotelsPrices: ReadonlyMap<number, number>;
  personalProfile: boolean;
  meta: TrackingMeta;
}

export interface SerpSearchFiltersTrackingOptions {
  searchUuid: string;
  uuid: string;
  autoChange: boolean;
  filtersModel: FiltersModel;
  products: readonly SerpProduct[];
  filteredProducts: readonly SerpProduct[];
  renderTime: number | null;
  meta: TrackingMeta;
}

const MS_IN_SECOND = 1000;

const toFlag = (value: boolean): TrackingFlag => (value ? 1 : 0);

function toSeconds(from: number | null, to: number | null): number {
  return from && to ? Math.round((to - from) / MS_IN_SECOND) : 0;
}

function countRangeDays({ from, to }: TrackingRange): number {
  const fromDate = parseDate(from);
  const toDate = parseDate(to);

  return fromDate && toDate ? getDateDifference(toDate, fromDate, 'days') + 1 : 0;
}

function toIndexedIds(prefix: string, ids: readonly number[], count: number): Record<string, number> {
  return Object.fromEntries(Array.from({ length: count }, (_, index) => [`${prefix}${index}`, ids[index] || 0]));
}

function toSelectionFlags(fields: Readonly<Record<string, number>>, selected: readonly number[]): TrackingData {
  return Object.fromEntries(Object.entries(fields).map(([field, id]) => [field, toFlag(selected.includes(id))]));
}

function getTrackingTourists({ touristsByRooms = [], ...firstRoom }: TouristGroup): TrackingTourists {
  const rooms = [firstRoom, ...touristsByRooms];
  const sum = (key: keyof TrackingRoom): number => rooms.reduce((total, room) => total + room[key], 0);

  return {
    adults: sum('adults'),
    kids: sum('kids'),
    infants: sum('infants'),
    rooms: touristsByRooms.length
      ? [{ adults: firstRoom.adults, kids: firstRoom.kids, infants: firstRoom.infants }, ...touristsByRooms]
      : 0,
  };
}

function getTrackingCriteria(criteria: SearchCriteria, range: TrackingRange | null): TrackingData {
  const params = buildSearchCriteriaParams(criteria);
  const hotelCategories = criteria.hotelCategories ?? [];
  const meals = criteria.meals ?? [];

  return {
    ...toIndexedIds('toCountry', params.countries, SERP_TRACKING_COUNTRIES_COUNT),
    ...toIndexedIds('toCity', params.resorts ?? [], SERP_TRACKING_RESORTS_COUNT),
    ...toIndexedIds('hotel', params.hotels ?? [], SERP_TRACKING_HOTELS_COUNT),
    fromCity: criteria.fromCity?.id,
    dateFrom: range?.from || 0,
    dateTo: range?.to || 0,
    nightFrom: criteria.nights?.from || 0,
    nightTo: criteria.nights?.to || 0,
    hotelCategory_all: toFlag(!hotelCategories.length),
    ...toSelectionFlags(SERP_TRACKING_HOTEL_CATEGORY_FIELDS, hotelCategories),
    meal_all: toFlag(!meals.length),
    ...toSelectionFlags(SERP_TRACKING_MEAL_FIELDS, meals),
    ...getTrackingTourists(params.touristGroup),
    operator: 0,
    directFlight: toFlag(Boolean(criteria.directFlight)),
  };
}

function toFilterOptionId({ groupId, key }: SerpTrackingFilterOption): string {
  return `${groupId}:${key}`;
}

const TRACKED_FILTER_OPTION_IDS: ReadonlySet<string> = new Set(
  Object.values(SERP_TRACKING_FILTER_FIELDS).flatMap((option) => (option ? [toFilterOptionId(option)] : [])),
);

function getSelectedFilterOptions(filtersModel: FiltersModel): SerpTrackingFilterOption[] {
  return SERP_FILTER_GROUPS.flatMap((group) =>
    isSearchFilterGroup(group) || group.control === 'budget'
      ? []
      : getSelectedFilterKeys(filtersModel, group).map((key) => ({ groupId: group.id, key })),
  );
}

const TRACKED_FILTER_GROUP_IDS: ReadonlySet<FilterGroupId> = new Set([
  FilterGroupId.Operators,
  FilterGroupId.Prepayments,
]);

function isTrackedFilterOption(option: SerpTrackingFilterOption): boolean {
  return TRACKED_FILTER_GROUP_IDS.has(option.groupId) || TRACKED_FILTER_OPTION_IDS.has(toFilterOptionId(option));
}

function getUntrackedFilterOptionIds(filtersModel: FiltersModel): string[] {
  return getSelectedFilterOptions(filtersModel)
    .filter((option) => !isTrackedFilterOption(option))
    .map((option) => toFilterOptionId(option))
    .sort();
}

function getFilterFlags(filtersModel: FiltersModel): Record<string, TrackingFlag> {
  const selectedIds = new Set(getSelectedFilterOptions(filtersModel).map((option) => toFilterOptionId(option)));

  return Object.fromEntries(
    Object.entries(SERP_TRACKING_FILTER_FIELDS).map(([field, option]) => [
      field,
      option ? toFlag(selectedIds.has(toFilterOptionId(option))) : 0,
    ]),
  );
}

function getTrackedFilters(filtersModel: FiltersModel): TrackingData {
  return {
    minPrice: filtersModel.budget.min,
    maxPrice: filtersModel.budget.max,
    operators: [...new Set(filtersModel.operators)].sort(),
    prepayments: [...new Set(filtersModel.prepayments)].sort(),
    ...getFilterFlags(filtersModel),
  };
}

function getOperatorFlags(products: readonly SerpProduct[], selected: readonly string[]): Record<string, TrackingFlag> {
  return Object.fromEntries([
    ...getOperatorsFilterOptions(getSerpResultsDictionaries(products).operators).map(
      ({ key }): [string, TrackingFlag] => [key, 0],
    ),
    ...selected.map((key): [string, TrackingFlag] => [key, 1]),
  ]);
}

function countOperators(products: readonly Product[]): Record<string, number> {
  const counts: Record<string, number> = {};

  products.forEach(({ operator }) => {
    if (operator) counts[operator.id] = (counts[operator.id] ?? 0) + 1;
  });

  return counts;
}

export function hasSearchCountry(criteria: SearchCriteria): boolean {
  return Boolean(buildSearchCriteriaParams(criteria).countries[0]);
}

export function getFirstHotelsPrices({ hotels, productsByHotel }: ProductSearchSnapshot<Product>): Map<number, number> {
  return new Map(
    hotels.flatMap(({ id }): [number, number][] => {
      const price = productsByHotel.get(id)?.[0]?.price;

      return price ? [[id, price]] : [];
    }),
  );
}

export function getSerpStartSearchData({
  productType,
  searchUuid,
  criteria,
  personalProfile,
  meta,
}: SerpStartSearchTrackingOptions): TrackingData {
  return {
    searchUuid,
    productType,
    hasPersonalProfile: toFlag(personalProfile),
    ...meta,
    ...getTrackingCriteria(criteria, criteria.dateRange),
  };
}

export function getSerpStartEmptySearchData({
  originalSearchUuid,
  ...options
}: SerpStartEmptySearchTrackingOptions): TrackingData {
  return {
    originalSearchUuid,
    productType: options.productType,
    ...getSerpStartSearchData(options),
  };
}

export function getSerpSearchResultsData({
  productType,
  result,
  criteria,
  firstHotelsPrices,
  personalProfile,
  meta,
}: SerpSearchResultsTrackingOptions): TrackingData {
  const { hotels, productsByHotel, timings } = result;
  const getCheapestProduct = (hotelId: number): Product | undefined => productsByHotel.get(hotelId)?.[0];
  const isFirstPriceKept = (hotelId: number): boolean => {
    const firstPrice = firstHotelsPrices.get(hotelId);

    return Boolean(firstPrice) && firstPrice === getCheapestProduct(hotelId)?.price;
  };

  return {
    productType,
    searchUuid: result.searchUuid,
    timeFirstTours: toSeconds(timings.startedAt, timings.firstResultsAt),
    timeSearchFinished: toSeconds(timings.startedAt, timings.finishedAt),
    hotelsLength: hotels.length,
    firstCheapestHotelsLength: hotels.filter(({ id }) => isFirstPriceKept(id)).length,
    scannedTours: result.scanned,
    operators: JSON.stringify(countOperators(result.products)),
    operatorsTime: JSON.stringify(result.progress.operators ?? {}),
    hasPersonalProfile: toFlag(personalProfile),
    error: toFlag(result.errors.some(({ phase }) => SERP_TRACKING_FAILED_PHASES.has(phase))),
    ...meta,
    ...getTrackingCriteria(criteria, criteria.dateRange),
    hotelsWithoutOil: hotels.filter(({ id }) => getCheapestProduct(id)?.oilTax === null).length,
  };
}

export function getCalendarTrackingData(
  { criteria, range, days, startedAt, finishedAt }: LowPriceCalendarLoad,
  meta: TrackingMeta,
): TrackingData {
  const requestedDays = countRangeDays(range);
  const pricedDays = days.filter(({ price }) => price).length;

  return {
    percentCovered: requestedDays ? Math.round((pricedDays / requestedDays) * 100) : 0,
    timeFinished: toSeconds(startedAt, finishedAt),
    hasPersonalProfile: 0,
    ...meta,
    ...getTrackingCriteria(criteria, range),
  };
}

export function isTrackedFiltersChange(previous: FiltersModel, filtersModel: FiltersModel): boolean {
  return (
    isEqual(getUntrackedFilterOptionIds(previous), getUntrackedFilterOptionIds(filtersModel)) &&
    !isEqual(getTrackedFilters(previous), getTrackedFilters(filtersModel))
  );
}

export function getSerpSearchFiltersData({
  searchUuid,
  uuid,
  autoChange,
  filtersModel,
  products,
  filteredProducts,
  renderTime,
  meta,
}: SerpSearchFiltersTrackingOptions): TrackingData {
  return {
    ab_tests: meta.ab_tests,
    operators: getOperatorFlags(products, filtersModel.operators),
    autoChange: toFlag(autoChange),
    hotelsLength: filteredProducts.length,
    hotelsWithoutOil: filteredProducts.filter((serpProduct) => serpProduct.products[0]?.oilTax === null).length,
    searchUuid,
    uuid,
    renderTime,
    customer_hash: meta.customer_hash,
    prepayments: filtersModel.prepayments.map(Number),
    minPrice: filtersModel.budget.min,
    maxPrice: filtersModel.budget.max,
    ...getFilterFlags(filtersModel),
  };
}

export function isGroupSearchSerpProduct({ products }: SerpProduct): boolean {
  return products.some(({ roomGroups }) => (roomGroups ?? []).length > 1);
}

export function toSerpHotelPositions(serpProducts: readonly SerpProduct[]): SerpHotelPosition[] {
  return serpProducts.map(({ hotel, cheapProduct }) => ({ id: hotel.id, price: cheapProduct?.price ?? null }));
}
